<script setup lang="ts">

import { ref, onBeforeMount, onBeforeUnmount } from "vue"
import { useGetDocs, postData, updateMeta, deleteData } from "../../mixins/modularFirebaseOperations"
import { modalClosed } from "../../mixins/visualFormatters"
import { propagateMetaToItems } from "@/composables/inventoryHelpers"
import AcMediumModal from "../reusable/teleports/acMediumModal.vue"
import InventoryMetaForm from "../reusable/form-widgets/inventoryMetaForm.vue"
import resetBackground from "@/mixins/resetBackground"
import removeBackground from "@/mixins/removeBackground"
import type { AcProductBrand } from "../../types/inventory"

const COLLECTION = "inventory/brands/list"

const isAddingMeta = ref<boolean>(false)
const editedMeta = ref<AcProductBrand | undefined>(undefined)
const { docs, isLoadingDocs } = useGetDocs<AcProductBrand>(COLLECTION)

const handleAddMeta = async function(meta: AcProductBrand) {
    const docRef = await postData(meta, COLLECTION)
    docs.value.push({ ...meta, id: docRef as string })
    isAddingMeta.value = false
    modalClosed()
}

const handleDelete = async function(metaId: string) {
    if (!confirm("Bu markayı silmek istediğinize emin misiniz?")) return
    await deleteData(COLLECTION, metaId)
    docs.value = docs.value.filter((meta) => meta.id !== metaId)
}

const handleUpdatedData = async function(meta: AcProductBrand) {
    await updateMeta(COLLECTION, meta.id!, meta)
    // Refresh the embedded brand snapshot on every item that references it.
    await propagateMetaToItems("productBrand", meta)
    const updatedMeta = docs.value.find((existingMeta) => existingMeta.id === meta.id)
    if (updatedMeta) {
        updatedMeta.name = meta.name
        updatedMeta.color = meta.color
    }
    editedMeta.value = undefined
    modalClosed()
}

onBeforeMount(() => {
  removeBackground.methods.removeBackground()
})

onBeforeUnmount(() => {
  resetBackground.methods.resetBackground()
})

</script>

<template>
    <div class="main-container mt-8 content-start items-start">
        <div class="flex col-start-1 col-end-13 justify-between items-center-w-full">
            <h2 class="ac-large-title">Markalar</h2>

            <div class="action-row col-start-1 col-end-13 flex items-center justify-end">
                <button type="button" @click="isAddingMeta = true" class="ac-add-button">
                    <img class="mr-4 filter-white" width="20" src="../../assets/icons/add.png" alt="">
                    Yeni Marka Ekle
                </button>
            </div>
        </div>
        <table v-if="!isLoadingDocs" class="alt-table col-start-1 col-end-13 w-full mt-5">
            <thead>
                <tr>
                    <th>Renk</th>
                    <th>Marka</th>
                    <th>İşlemler</th>
                </tr>
            </thead>
            <tr class="hover:bg-gray-200 relative cursor-pointer" v-for="meta in docs" :key="meta.id">
                <td class="text-overflow w-24">
                    <div class="w-6 h-6 rounded-full" :style="{ backgroundColor: meta.color }"></div>
                </td>
                <td class="text-overflow"> {{ meta.name }}</td>
                <td class="flex">
                    <button type="button" class="table-button" @click="handleDelete(meta.id!)">
                        Sil
                    </button>
                    <button type="button" class="table-button" @click="editedMeta = meta">
                        Düzenle
                    </button>
                </td>
            </tr>
        </table>

    <teleport to="body">
        <transition
            appear
            type="animation"
            enter-active-class="animate__animated animate__fadeIn animate__fast"
            leave-active-class="animate__animated animate__fadeOut animate__faster"
        >
            <ac-medium-modal class="lg:!top-44 overflow-y-scroll" @modal-closed="isAddingMeta = false; editedMeta = undefined" v-if="isAddingMeta || editedMeta" height="fit-content" width="40vw" title="Marka">
                <inventory-meta-form
                metaTitle="Marka"
                :hasColor="true"
                :existingMeta="editedMeta"
                @meta-added="handleAddMeta($event)"
                @meta-updated="handleUpdatedData($event)"
                />
            </ac-medium-modal>
       </transition>
  </teleport>
    </div>
</template>

<style lang="scss">

@import "../../styles/global/helpers.scss";
@import "../../styles/global/buttons.scss";
@import "../../styles/global/tables.scss";

</style>
